import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { actionQueries } from '@/features/actions/queries'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { graylogQueries } from '@/features/graylog/queries'
import { playbookQueries } from '@/features/playbooks/queries'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { TableSkeleton } from '@/components/skeletons/table-skeleton'
import { Skeleton } from '@/components/ui/skeleton'

const AutomationSettingsDetailsForm = lazy(
	() => import('@/features/graylog/automation-settings/component/form'),
)

const searchSchema = z.object({
	event_id: z.string().optional(),
	playbook_title: z.string().optional(),
	action_name: z.string().optional(),
})

export const Route = createFileRoute(
	'/_dashboard/graylog/automation-settings/new/',
)({
	validateSearch: zodValidator(searchSchema),

	beforeLoad: () => roleAuthGuard(ROLES.Analyst),

	loader: ({ context: { queryClient } }) => {
		const tenant_id = getCurrentTenant()

		// Prefetch list of events and responses
		if (tenant_id) {
			queryClient.prefetchQuery(actionQueries.list(tenant_id))
			queryClient.prefetchQuery(graylogQueries.events(tenant_id))
			queryClient.prefetchQuery(playbookQueries.list(tenant_id))
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Create New Automation | Graylog | BitLyft AIR®',
			},
		],
	}),

	component: AutomationSettingsEvent,
	pendingComponent: AutomationSettingsEventFallback,
	errorComponent: RouteErrorComponent,
})

function AutomationSettingsEvent() {
	return (
		<div className="flex w-full flex-col items-start justify-start gap-2">
			<ErrorBoundary>
				<Suspense fallback={<TableSkeleton rows={10} />}>
					<AutomationSettingsDetailsForm ruleId={null} />
				</Suspense>
			</ErrorBoundary>
		</div>
	)
}

function AutomationSettingsEventFallback() {
	return (
		<div className="flex w-full flex-row items-center justify-between">
			<Skeleton className="h-12 w-1/2" />
			<Skeleton className="h-12 w-1/2" />
		</div>
	)
}
